/* Speedometer for one % against its target (credit-score style): a half dial split into Critical / Below target / On target
   zones, a needle at the value, a target tick, and the value + status in words under it, so it never relies on colour.
   On load (and when a filter changes the value) the needle swings in and settles, the dial lights up behind it in each
   zone's colour, and the number counts up; all skipped under reduced motion (podMotion.js).
   Values usually sit at 80–100%, so the dial starts at a round floor below both the value and (target − 30) and both end
   labels are printed — the reader always sees where the scale starts. */
import React from 'react'
import { Box, ButtonBase, Typography, useTheme } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { TONE } from './podUtils'
import { CountUp, easeOutBack, enter, fadeUp, useTween } from './podMotion'

const W = 240
const CX = 120
const CY = 122
const R = 96
const STROKE = 18
const WARN_BAND = 10 // points below target that still count as "below target", not critical

/** Status of a % against its target: good / warning / critical (null when there is no value). */
export const gaugeLevel = (value, target) => {
  if (value == null) return null
  if (value >= target) return 'success'
  return value >= target - WARN_BAND ? 'warning' : 'error'
}

const LEVEL = {
  success: { Icon: CheckCircleOutlineIcon, text: () => 'On target' },
  warning: { Icon: WarningAmberOutlinedIcon, text: (gap) => `${gap} pts below target` },
  error: { Icon: ErrorOutlineIcon, text: (gap) => `Critical · ${gap} pts below target` },
}

const round1 = (n) => Math.round(n * 10) / 10

export default function PodGauge({ title, value, target, sub, action, onAction, loading }) {
  const theme = useTheme()
  const ink = theme.palette.text.primary
  const muted = theme.palette.text.secondary
  const floor = value == null ? 0 : Math.max(0, Math.floor(Math.min(value, target - 30) / 10) * 10)
  const span = 100 - floor
  const angle = (v) => Math.PI * (1 - (Math.min(Math.max(v, floor), 100) - floor) / span)
  const pt = (v, r = R) => [CX + r * Math.cos(angle(v)), CY - r * Math.sin(angle(v))]
  const arc = (a, b) => {
    const [x1, y1] = pt(a); const [x2, y2] = pt(b)
    return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`
  }
  // 2px surface gap between zones, in % units
  const gap = (2 / R / Math.PI) * span
  const zones = [
    { key: 'error', from: floor, to: target - WARN_BAND },
    { key: 'warning', from: target - WARN_BAND, to: target },
    { key: 'success', from: target, to: 100 },
  ].map((z) => ({ ...z, from: Math.max(z.from, floor), to: Math.min(z.to, 100) })).filter((z) => z.to - z.from > gap)

  // needle swings past and settles; the lit arc and the marker follow it but never pass the real value
  const { value: needleV } = useTween(value, { from: floor, ms: 1400, ease: easeOutBack })
  const lit = needleV == null ? null : Math.min(needleV, value)
  const ticks = Array.from({ length: 11 }, (_x, i) => floor + (span * i) / 10)

  const level = gaugeLevel(value, target)
  const L = level && LEVEL[level]
  const tone = TONE[level || 'neutral']
  const needleDeg = needleV == null ? -90 : 90 - (angle(needleV) * 180) / Math.PI
  const [mx, my] = lit == null ? [0, 0] : pt(lit)
  const [tx1, ty1] = pt(target, R - STROKE / 2 - 5)
  const [tx2, ty2] = pt(target, R + STROKE / 2 + 3)
  const [lx, ly] = pt(target, R - STROKE / 2 - 14)
  const gapPts = value == null ? 0 : round1(target - value)
  const label = value == null ? `${title}: no data` : `${title}: ${value}%, target ${target}%. ${L.text(gapPts)}.`

  return (
    <Box sx={{ textAlign: 'center', minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.secondary' }}>{title}</Typography>
      <Box component='svg' viewBox={`0 0 ${W} 140`} role='img' aria-label={label} sx={{ width: '100%', maxWidth: 280, display: 'block', mx: 'auto', mt: 0.5 }}>
        {/* zones (pale), then lit up to the value in each zone's own colour */}
        {zones.map((z, i) => {
          const a = z.from + (i ? gap / 2 : 0)
          const b = z.to - (i < zones.length - 1 ? gap / 2 : 0)
          const litTo = lit == null ? a : Math.min(b, lit)
          return (
            <g key={z.key}>
              <path d={arc(a, b)} fill='none' stroke={TONE[z.key].border} strokeWidth={STROKE} />
              {litTo - a > 0.05 && <path d={arc(a, litTo)} fill='none' stroke={TONE[z.key].fg} strokeWidth={STROKE} />}
            </g>
          )
        })}
        {/* speedometer ticks every tenth of the dial */}
        {ticks.map((v) => {
          const [x1, y1] = pt(v, R - STROKE / 2 - 3); const [x2, y2] = pt(v, R - STROKE / 2 - 7)
          return <line key={v} x1={x1} y1={y1} x2={x2} y2={y2} stroke={muted} strokeOpacity={0.5} strokeWidth={1.25} strokeLinecap='round' />
        })}
        {/* marker at the tip of the lit arc */}
        {lit != null && <circle cx={mx} cy={my} r={STROKE / 2 - 1} fill={theme.palette.background.paper} stroke={TONE[gaugeLevel(lit, target)].fg} strokeWidth={3} />}
        {/* target tick + label */}
        <line x1={tx1} y1={ty1} x2={tx2} y2={ty2} stroke={ink} strokeWidth={2.5} strokeLinecap='round' />
        <text x={lx} y={ly} fontSize='10.5' fontWeight='700' fill={muted} textAnchor={lx < CX - 20 ? 'start' : lx > CX + 20 ? 'end' : 'middle'}
          dominantBaseline='middle'>Target {target}%</text>
        {/* scale ends */}
        <text x={CX - R} y={CY + 16} fontSize='10.5' fill={muted} textAnchor='middle'>{floor}%</text>
        <text x={CX + R} y={CY + 16} fontSize='10.5' fill={muted} textAnchor='middle'>100%</text>
        {/* needle */}
        {value != null && (
          <Box component='g' sx={{ transform: `rotate(${needleDeg}deg)`, transformOrigin: `${CX}px ${CY}px`, transformBox: 'view-box' }}>
            <path d={`M ${CX - 4} ${CY} L ${CX} ${CY - R + STROKE / 2 + 6} L ${CX + 4} ${CY} Z`} fill={ink} />
          </Box>
        )}
        <circle cx={CX} cy={CY} r={8} fill={ink} />
        <circle cx={CX} cy={CY} r={3} fill={theme.palette.background.paper} />
      </Box>
      <Typography sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1.1, mt: -0.5, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
        {loading && value == null ? '…' : value == null ? '—' : <><CountUp value={value} decimals={value % 1 ? 1 : 0} ms={1400} />%</>}
      </Typography>
      {L ? (
        <Box key={value} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.75, px: 1, py: 0.25, borderRadius: 5, bgcolor: tone.bg,
          border: 1, borderColor: tone.border, color: tone.fg, ...enter(fadeUp, 450, 1000) }}>
          <L.Icon sx={{ fontSize: 16 }} aria-hidden />
          <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{L.text(gapPts)}</Typography>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.75 }}>No data for these dates</Typography>
      )}
      {sub && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1 }}>{sub}</Typography>}
      {action && onAction && (
        <ButtonBase onClick={onAction} sx={{ mt: 0.75, px: 1, py: 0.5, borderRadius: 1, fontSize: 13, fontWeight: 600, color: 'primary.main', minHeight: 32,
          '&:hover': { bgcolor: 'action.hover' }, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}>
          {action} ›
        </ButtonBase>
      )}
    </Box>
  )
}
